import { useCallback, useEffect, useState } from 'react';
import useImage from 'use-image';
import { useDebouncedLoading } from '../../../../features/commonUI/hooks/useDebouncedLoading';
import { useOpenCVWorker } from './hooks/useOpenCVWorker';
import { ColorCorrectionConfig, ColorCorrectionState } from './types';

type UseColorCorrectionReturn = {
  finalImage: HTMLImageElement | null;
  colorCorrectionState: ColorCorrectionState;
};

/**
 * Custom React hook for performing color correction on an image using a web worker (OpenCV).
 *
 * This hook loads an image from the provided `src` URL, processes it asynchronously using
 * an OpenCV worker, and returns the processed image along with the current color correction state.
 *
 * @param {string | undefined} src - The source URL of the image to be color corrected.
 * @param {ColorCorrectionConfig} config - Configuration object specifying the image resolution.
 *
 * @returns {UseColorCorrectionReturn} An object containing:
 *   - `finalImage`: The processed (color-corrected) HTMLImageElement, or null if not available.
 *   - `colorCorrectionState`: The current state of the color correction process, including loading and error status.
 *
 * @example
 * const { finalImage, colorCorrectionState } = useColorCorrection(imageUrl, { imageResolution: '720' });
 */
export const useColorCorrection = (
  src: string | undefined,
  config: ColorCorrectionConfig
): UseColorCorrectionReturn => {
  const [finalImage, setFinalImage] = useState<HTMLImageElement | null>(null);

  const [originalImage, imageLoadingStatus] = useImage(src || '', 'anonymous');

  // Color correction state
  const [colorCorrectionState, setColorCorrectionState] =
    useState<ColorCorrectionState>({
      finalClipLimit: 0,
      error: false,
      isLoading: true,
    });

  useEffect(() => {
    // reset final image
    setFinalImage(null);
  }, [src]);

  /* OpenCV Worker Event Handlers */

  const handleProcessingStart = useCallback(() => {
    // Only set loading if we're not already loading and we have an image to process
    setColorCorrectionState((prev: ColorCorrectionState) => {
      if (prev.isLoading) {
        return prev;
      }
      return { finalClipLimit: 0, error: false, isLoading: true };
    });
  }, []);

  const handleProcessingComplete = useCallback(
    (
      finalClipLimit: number,
      error: boolean,
      processedImage: HTMLImageElement | null
    ) => {
      setColorCorrectionState({
        finalClipLimit,
        error,
        isLoading: false,
      });

      if (processedImage && !error) {
        setFinalImage(processedImage);
        return;
      }

      setFinalImage(null);
    },
    []
  );

  const handleProcessingError = useCallback(() => {
    setColorCorrectionState({
      finalClipLimit: 0,
      error: true,
      isLoading: false,
    });
    // clear image
    setFinalImage(null);
  }, []);

  /* OpenCV Worker Hook */
  const { processImage, isWorkerReady } = useOpenCVWorker(
    {
      imageResolution: config.imageResolution,
      skip: false,
    },
    handleProcessingStart,
    handleProcessingComplete,
    handleProcessingError
  );

  useEffect(() => {
    if (!isWorkerReady) {
      console.debug(
        '⚠️ useColorCorrection useEffect called, but worker is not ready'
      );
      return;
    }

    if (imageLoadingStatus === 'loading') {
      console.debug(
        '⚠️ useColorCorrection useEffect called, but image is still loading'
      );
      return;
    }

    if (originalImage && imageLoadingStatus === 'loaded') {
      processImage(originalImage);
    }
  }, [originalImage, processImage, isWorkerReady, imageLoadingStatus]);

  const colorCorrectionLoading = useDebouncedLoading(
    colorCorrectionState.isLoading || imageLoadingStatus === 'loading',
    300,
    true
  );

  return {
    finalImage: colorCorrectionState.isLoading ? null : finalImage,
    colorCorrectionState: {
      finalClipLimit: colorCorrectionState.finalClipLimit,
      isLoading: colorCorrectionLoading,
      error: colorCorrectionState.error || imageLoadingStatus === 'failed',
    },
  };
};
